import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import times from 'lodash/times';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { GOAL_ICON_MAP } from '../../../Goals/GoalPage.constants';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Lozenge } from '../../../../CommonComponents/OPL/Lozenge/Lozenge';
import { GoalSummary } from '../../useGoalsSummary';
import { useClientId } from '../../../../_hooks/useClientId';
import { externalHostSelector as selectExternalHostSelector } from '../../../../ConsumerArea/ConsumerAreaSelector';
import { EXTERNAL_HOST } from '../../../../ConsumerArea/ConsumerAreaConstants';
import { Card } from '../../../../CommonComponents/OPL/Card/Card';
import { VisualPickerButtonContainer } from '../../../../CommonComponents/OPL/VisualPickerButton/VisualPickerButtonContainer';
import { getGoalPageName } from '../../../Goals/utils/getGoalPageName';
import { GoalTypeDTO } from '../../../types/goalsDTO';
import { Shimmer } from '../../../../CommonComponents/OPL/Shimmer/Shimmer';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { selectHasMobileNavigation } from '../../../../CommonSelectors/CommonAreaSelector';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { CustomIcon } from './components/CustomIcon/CustomIcon';

interface Props {
  isLoading?: boolean;
  isError?: boolean;
  onRetry?(): void;
  goal: GoalSummary;
}

export const GoalCard = ({ isLoading, isError, onRetry, goal }: Props) => {
  return (
    <CardContainer isLoading={isLoading} isError={isError} goal={goal}>
      <Box display="flex" flex={1} flexDirection="column" height="100%">
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="16">
          <Box display="flex" alignItems="center" gridGap="8">
            {goal.GoalOrigin ? (
              <CustomIcon icon={goal.GoalOrigin} />
            ) : (
              <Icon className={GOAL_ICON_MAP[goal.GoalType]} fontSize="h3" color="neutral.500" />
            )}

            <Box gridGap="4">
              <Text fontSize="h5" fontWeight={600}>
                {goal.GoalName}
              </Text>

              {goal.GoalDescription && (
                <Text fontWeight={400} color="neutral.500">
                  {goal.GoalDescription}
                </Text>
              )}
            </Box>
          </Box>

          {goal.IsCompleted ? (
            <FundedLozenge isLoading={isLoading} isError={isError} isFunded={goal.IsFunded} />
          ) : (
            <Lozenge variantColor="default">Incomplete</Lozenge>
          )}
        </Box>

        <GoalResults
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
          results={goal.GoalResults}
        />
      </Box>
    </CardContainer>
  );
};

interface CardContainerProps {
  isLoading?: boolean;
  isError?: boolean;
  goal: GoalSummary;
  children: ReactNode;
}

const CardContainer = ({ isLoading, isError, children, goal }: CardContainerProps) => {
  const clientId = useClientId();

  const externalHost = useSelector(selectExternalHostSelector);

  const isExternal = externalHost === EXTERNAL_HOST.ELEMENT;

  const hasMobileNavigation = useSelector(selectHasMobileNavigation);

  if (isExternal && goal.GoalType !== GoalTypeDTO.Decumulation) {
    return (
      <Card px="16" py="24" height="100%">
        {children}
      </Card>
    );
  }

  return (
    <VisualPickerButtonContainer
      onClick={() => {
        if (isLoading || isError) {
          return;
        }

        if (!hasMobileNavigation) {
          ADV.util.Router.go2(getGoalPageName(goal.GoalType as GoalTypeDTO), {
            clientId,
            goalId: goal.GoalId,
          });
        }
      }}
      style={{
        width: '100%',
        height: '100%',
        pointerEvents: isLoading || isError ? 'none' : 'unset',
      }}
    >
      {children}
    </VisualPickerButtonContainer>
  );
};

interface FundedLozengeProps {
  isLoading?: boolean;
  isError?: boolean;
  isFunded: boolean;
}

const FundedLozenge = ({ isLoading, isError, isFunded }: FundedLozengeProps) => {
  if (isLoading) {
    return <Shimmer width={75} height={24} />;
  }

  if (isError) {
    return null;
  }

  return (
    <Lozenge variantColor={isFunded ? 'primary' : 'default'}>
      {isFunded ? 'Funded' : 'Not Funded'}
    </Lozenge>
  );
};

interface GoalResultsProps {
  isLoading?: boolean;
  isError?: boolean;
  onRetry?(): void;
  results: { label: string; value: string }[];
}

const GoalResults = ({ isLoading, isError, onRetry, results }: GoalResultsProps) => {
  if (isLoading) {
    return (
      <Box>
        {times(4).map(key => (
          <Box display="flex" justifyContent="space-between" mb="8" key={key}>
            <Shimmer width={100} height={20} />

            <Shimmer width={100} height={20} />
          </Box>
        ))}
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p="4">
        <ErrorState onAction={onRetry} />
      </Box>
    );
  }

  return (
    <Box
      as="ul"
      p={0}
      css={`
        list-style: none;
      `}
      mt="auto"
    >
      {results.map(({ label, value }) => (
        <Box
          key={label}
          as="li"
          display="flex"
          justifyContent="space-between"
          mb="8"
          style={{ gap: 16 }}
        >
          <Text style={{ whiteSpace: 'nowrap' }}>{label}</Text>

          <Text fontWeight={500} textAlign="right">
            {value}
          </Text>
        </Box>
      ))}
    </Box>
  );
};
